import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { planService } from '../../application/PlanService';
import { Driver } from '../../domain/driver/Driver';
import { Plan } from '../../domain/plan/Plan';
import { PlanStatus } from '../../domain/plan/PlanStatus';
import { Project } from '../../domain/project/Project';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveOrganization } from '../organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../project/useRetrieveActiveProjectGeoFences';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUserCredentials } from '../user/useRetrieveUserCredentials';

type UseCreatePlanProps = {
  label?: string;
  status: PlanStatus;
  executionDate: Date;
  drivers: Driver[];
};

type UseCreatePlanResponseProps = ServiceResponse<
  ({ label, executionDate }: UseCreatePlanProps) => void,
  string | undefined
>;

// Hooks act as 'Adapter' layer.
const useCreatePlan = ({ project }: { project?: Project }): UseCreatePlanResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: organization } = useRetrieveOrganization();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: geoFences } = useRetrieveActiveProjectGeoFences();
  const [planId, setPlanId] = useState<string | undefined>(undefined);

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ label, status, executionDate, drivers }: UseCreatePlanProps): Promise<Plan> => {
      const plan = Plan.create({
        label,
        drivers,
        status,
        execution_date: executionDate,
        project_id: project!.id,
        organization_id: organization!.id,
        geo_fences: userCredentials?.subscription.test(`planner.geofences`) ? geoFences ?? [] : [],
      });
      return planService.create({ plan, project: activeProject! });
    },
    {
      onSuccess: (plan) => {
        setPlanId(plan.id);
      },
      onError: (err: any) => {
        console.info(`Create  Plan Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    data: planId,
    run: ({ label, status, executionDate, drivers }: UseCreatePlanProps) =>
      mutate({ label, status, executionDate, drivers }),
  };
};

export default useCreatePlan;
